import type { Channel, KeySource, Provider } from '../enrichment/enrichmentApi';
import { isVerified, type LookupCompanySource, type LookupResult } from './lookupApi';

export type StepStatus = 'found' | 'noMatch' | 'notNeeded' | 'skippedForCredits';

export type WaterfallStep = {
	/** Airscale is its own first step where it is available: it answers before the saved order runs. */
	provider: Provider | 'airscale';
	keySource: KeySource | null;
	/** Null until a person lookup has asked this channel. */
	status: StepStatus | null;
	verified: boolean;
};

/** A shown result, with the channels the rep asked for when it ran. */
export type LookupRun = { result: LookupResult; find: Channel[] };

/**
 * What each step of a channel's saved order did for the shown result, read back from the value's
 * `provider`: the waterfall stops at the first step that finds something, so the steps before it
 * found nothing and the ones after it never ran. A pasted value needs no step at all, and neither
 * does one Airscale found before the waterfall ran (WEB-13238).
 *
 * Only what can run is listed (WEB-13771): Airscale first where it is available, and no provider
 * the account has no key for - a step that never runs says nothing about the result.
 */
export const waterfallSteps = (
	order: Provider[],
	keys: Record<Provider, KeySource> | null,
	channel: Channel,
	run: LookupRun | null,
	withAirscale = false
): WaterfallStep[] => {
	const person = run?.result.person ?? null;
	const asked = !!person && !!run?.find.includes(channel);
	const skippedForCredits = !!run?.result.skippedForCredits;
	const value = person ? person[channel] : null;
	const beforeWaterfall = value?.provider === 'input' || value?.provider === 'airscale';
	const foundAt = value && !beforeWaterfall ? order.indexOf(value.provider) : -1;

	const airscale: WaterfallStep[] = withAirscale
		? [
				{
					provider: 'airscale',
					keySource: null,
					status: !asked
						? null
						: value?.provider === 'airscale'
						? 'found'
						: skippedForCredits
						? 'skippedForCredits'
						: value?.provider === 'input'
						? 'notNeeded'
						: 'noMatch',
					verified: value?.provider === 'airscale' && isVerified(value)
				}
		  ]
		: [];

	const steps = order.map((provider, index): WaterfallStep => {
		const keySource = keys ? keys[provider] : null;
		const status = ((): StepStatus | null => {
			if (!asked) return null;
			// The server drops Upsales-key providers once the AI credits are used up (WEB-12978).
			if (keySource === 'default' && skippedForCredits) return 'skippedForCredits';
			if (beforeWaterfall) return 'notNeeded';
			if (index === foundAt) return 'found';
			return foundAt === -1 || index < foundAt ? 'noMatch' : 'notNeeded';
		})();
		return { provider, keySource, status, verified: status === 'found' && !!value && isVerified(value) };
	});
	return [...airscale, ...steps.filter(step => step.keySource !== 'none')];
};

/**
 * The sources a company lookup asks, in order (WEB-13098, WEB-13378): an org number only the register
 * and the CRM; a website those, then Airscale for an account with it, then Apollo; a LinkedIn company
 * page is read by Airscale, whose website the register and the CRM are asked about first.
 */
const sourcesFor = (kind: LookupResult['kind'], withAirscale: boolean): LookupCompanySource[] =>
	kind === 'orgNumber'
		? ['register', 'crm']
		: kind === 'linkedinCompany'
		? ['register', 'crm', 'airscale']
		: withAirscale
		? ['register', 'crm', 'airscale', 'apollo']
		: ['register', 'crm', 'apollo'];

export type CompanySourceStep = {
	source: LookupCompanySource;
	status: 'found' | 'noMatch' | 'notNeeded';
};

/**
 * Which sources can run on this account (WEB-13771): Airscale only when it is available - the flag,
 * the admin's toggle, not EU ONLY - and Apollo only with a key. Neither is listed otherwise.
 */
export type CompanySourceAvailability = { airscale: boolean; apollo: boolean };

/**
 * Where a company lookup found the company (WEB-13098). The server asks the register, then the
 * CRM, then - for a website only - Apollo, and stops at the first that has it. Empty for a person.
 */
export const companySourceSteps = (
	run: LookupRun | null,
	available: CompanySourceAvailability = { airscale: false, apollo: true }
): CompanySourceStep[] => {
	const company = run && !run.result.person ? run.result.company : null;
	if (!run || !company) return [];
	// A source the account cannot run is left out rather than listed as 'not needed' (WEB-13771).
	const sources = sourcesFor(run.result.kind, available.airscale).filter(
		source => source !== 'apollo' || available.apollo || company.source === 'apollo'
	);
	const foundAt = sources.indexOf(company.source);
	return sources.map((source, index) => ({
		source,
		status: index === foundAt ? 'found' : index < foundAt ? 'noMatch' : 'notNeeded'
	}));
};
